import { IS_MIX_CATALOG, MIX_REGISTERED_SLUGS } from "@/config/browserBoard";
import { DockingOnboardingWall } from "@/features/mix-onboarding/components/DockingOnboardingWall";
import {
  type PwaInstallPromptStore,
  pwaInstallPromptStore,
} from "@/features/mix-onboarding/lib/pwaInstallPrompt";
import { useDockingGatePresenter } from "@/features/mix-onboarding/useDockingGatePresenter";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import {
  resolveEnabledMixSlugs,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";

// パネル本体はlazyにしない。板選択カードを設定画面(初期グラフにある
// settingsTabs chunk)と共用しており、分けても共有chunkが初期グラフへ残るうえ、
// chunk境界が増えて初期グラフのファイル数とpreloadが増える(実測 35→41件)。

interface DockingOnboardingGateProps {
  /** ゲート通過後に描画するルート配下。パネル表示中はマウントしない。 */
  children: React.ReactNode;
  /** ルートが実際に採用したPC/SP表示。Wall側で再判定しない。 */
  isDesktop: boolean;
  /** テスト用の注入点。通常はruntime configから取る。 */
  isMixCatalog?: boolean;
  registeredSlugs?: readonly string[];
  installPromptStore?: PwaInstallPromptStore;
  /** TanStack Routerが返す、mix mountを除いたpathname。 */
  pathname?: string;
}

/**
 * 総合カタログマウントの初回パネルの出し分け。`/mix` 配下のどのページから
 * 入ってもゲートを通すため、ルートレイアウトからマウントする。
 *
 * パネルを被せるのではなく`children`ごと差し替える。重ねるだけだと年齢確認中
 * (「いいえ」のままでも)にカタログのルートがマウントし、useThreadsのクエリと
 * サムネイルの取得が走ってしまう。R-18板を含む入口ゲートでは、見た目を隠す
 * だけでなく、年齢確認または非R18板だけの選択が済むまで取得自体を止める必要がある。
 */
export const DockingOnboardingGate: React.FunctionComponent<
  DockingOnboardingGateProps
> = ({
  children,
  isDesktop,
  isMixCatalog = IS_MIX_CATALOG,
  registeredSlugs: runtimeSlugs = MIX_REGISTERED_SLUGS,
  installPromptStore = pwaInstallPromptStore,
  pathname = "/",
}: DockingOnboardingGateProps) => {
  const registeredSlugs = useMixCandidateSlugs(runtimeSlugs);
  const boardSettings = useMixStore((state) => state.boardSettings);
  const presentation = useDockingGatePresenter(
    isMixCatalog,
    isDesktop,
    pathname,
    installPromptStore,
    resolveEnabledMixSlugs(registeredSlugs, boardSettings),
  );
  if (!presentation.isOpen)
    return <ComponentScope node={presentation.node}>{children}</ComponentScope>;

  return (
    <ComponentScope node={presentation.node}>
      <DockingOnboardingWall
        isMobileDisplay={!isDesktop}
        registeredSlugs={registeredSlugs}
        installPromptStore={installPromptStore}
        requiresAdultConfirmation={presentation.requiresAdultConfirmation}
        onComplete={presentation.onComplete}
      />
    </ComponentScope>
  );
};
